<template>
  <svg class="heat-map-overlay" v-if="showHeatMap && hasMetrics">
    <defs>
      <filter id="heat-blur" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur in="SourceGraphic" stdDeviation="8" />
      </filter>
      <radialGradient id="heat-gradient">
        <stop offset="0%" stop-color="white" stop-opacity="0.8" />
        <stop offset="50%" stop-color="white" stop-opacity="0.3" />
        <stop offset="100%" stop-color="white" stop-opacity="0" />
      </radialGradient>
      <!-- Animated gradient for color shifting -->
      <radialGradient id="heat-color-gradient" cx="50%" cy="50%" r="100%">
        <stop offset="0%" stop-color="#00ffcc" stop-opacity="0.8">
          <animate attributeName="stop-color" 
            values="#00ffcc;#ff6b6b;#4ecdc4;#ffe66d;#00ffcc" 
            dur="8s" 
            repeatCount="indefinite" />
        </stop>
        <stop offset="100%" stop-color="#ff6b6b" stop-opacity="0.3">
          <animate attributeName="stop-color" 
            values="#ff6b6b;#4ecdc4;#ffe66d;#00ffcc;#ff6b6b" 
            dur="8s" 
            repeatCount="indefinite" />
        </stop>
      </radialGradient>
      <!-- Create mask from heat points -->
      <mask id="heat-mask">
        <g v-for="point in heatPoints" :key="`mask-${point.id}`">
          <rect
            v-if="point.width && point.height"
            :x="point.x - point.width / 2"
            :y="point.y - point.height / 2"
            :width="point.width"
            :height="point.height"
            :rx="16"
            :ry="16"
            fill="white"
            :opacity="point.intensity * 0.8"
            filter="url(#heat-blur)" />
          <circle
            v-else
            :cx="point.x"
            :cy="point.y"
            :r="point.radius"
            fill="white"
            :opacity="point.intensity * 0.8"
            filter="url(#heat-blur)" />
        </g>
      </mask>
    </defs>
    <!-- Base heat map layer -->
    <g class="heat-base-layer">
      <g v-for="point in heatPoints" :key="point.id">
        <rect
          v-if="point.width && point.height"
          :x="point.x - point.width / 2"
          :y="point.y - point.height / 2"
          :width="point.width"
          :height="point.height"
          :rx="16"
          :ry="16"
          :fill="getHeatColor(point.intensity)"
          :opacity="point.intensity * 0.4"
          filter="url(#heat-blur)"
          class="heat-point" />
        <circle
          v-else
          :cx="point.x"
          :cy="point.y"
          :r="point.radius"
          :fill="getHeatColor(point.intensity)"
          :opacity="point.intensity * 0.4"
          filter="url(#heat-blur)"
          class="heat-point" />
      </g>
    </g>
    
    <!-- Animated overlay layer with color shifting -->
    <g class="heat-glow-layer">
      <rect 
        x="0" 
        y="0" 
        width="100%" 
        height="100%" 
        fill="url(#heat-color-gradient)"
        opacity="0.3"
        mix-blend-mode="overlay"
        mask="url(#heat-mask)" />
    </g>
  </svg>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import { useFlowBuilderStore } from '../stores/flowBuilderStore'
import { useFlowMetricsStore } from '../stores/flowMetricsStore'

const props = defineProps<{
  connections: any[] // Connection data from flow builder
  nodeElements: Map<
    string,
    { x: number; y: number; width: number; height: number }
  > // Node positions
}>()

const flowBuilderStore = useFlowBuilderStore()
const flowMetricsStore = useFlowMetricsStore()

// Get the flow prop injected from parent
const flow = inject<any>('flow')

const showHeatMap = computed(() => flowBuilderStore.showHeatMap)
const nodeMetrics = computed(() => {
  if (!flow?.value?.id) return {}

  // Get metrics from the flow query data
  const query = flowMetricsStore.flowQueries[flow.value.id]
  if (query?.data?.value?.nodeMetrics) {
    return query.data.value.nodeMetrics
  }

  // Fall back to cache
  return flowMetricsStore.nodeMetricsCache[flow.value.id] || {}
})

// Check if we have any metrics data
const hasMetrics = computed(() => {
  if (!nodeMetrics.value || typeof nodeMetrics.value !== 'object') {
    return false
  }
  return Object.keys(nodeMetrics.value).some(
    (nodeId) => nodeMetrics.value[nodeId]?.visitorCount > 0
  )
})

// Generate heat color based on intensity
function getHeatColor(intensity: number): string {
  if (intensity < 0.3) {
    // Cold - blue/green
    return `rgb(0, ${Math.floor(150 + intensity * 350)}, ${Math.floor(255 - intensity * 100)})`
  } else if (intensity < 0.6) {
    // Warm - yellow/orange
    return `rgb(${Math.floor(255)}, ${Math.floor(255 - (intensity - 0.3) * 300)}, 0)`
  } else {
    // Hot - orange/red
    return `rgb(255, ${Math.floor(100 - (intensity - 0.6) * 250)}, 0)`
  }
}

// Create heat map data points from nodes and connections
const heatPoints = computed(() => {
  const points: Array<{
    id: string
    x: number
    y: number
    intensity: number
    radius: number
    width?: number
    height?: number
  }> = []

  if (!props.nodeElements || !nodeMetrics.value) return points

  // Find max visitor count for normalization
  let maxVisitors = 1
  for (const metrics of Object.values(nodeMetrics.value) as any[]) {
    if (metrics?.visitorCount > maxVisitors) {
      maxVisitors = metrics.visitorCount
    }
  }

  // Track processed nodes to avoid duplicates
  const processedNodes = new Set<string>()

  // Add heat overlays for all nodes with traffic
  props.nodeElements.forEach((position, nodeId) => {
    // Skip split labels and widgets for node overlays
    if (nodeId.includes('split-label') || nodeId.includes('widget')) return

    // Skip if already processed
    if (processedNodes.has(nodeId)) return
    processedNodes.add(nodeId)

    const metrics = nodeMetrics.value[nodeId]
    if (metrics && metrics.visitorCount > 0) {
      // Use logarithmic scale for better visualization of differences
      const intensity =
        Math.log(metrics.visitorCount + 1) / Math.log(maxVisitors + 1)

      //      Add a heat overlay for the node
      points.push({
        id: `node-overlay-${nodeId}`,
        x: position.x + position.width / 2,
        y: position.y + position.height / 2,
        intensity,
        radius: Math.max(position.width, position.height) * 0.5, // Smaller radius
        width: position.width * 1.0, // Slightly smaller than the actual node
        height: position.height * 1.0
      })
    }
  })

  // Track which connections have traffic by checking connected nodes
  if (props.connections && props.nodeElements) {
    props.connections.forEach((connection, index) => {
      if (connection.path && connection.path.length >= 2) {
        const path = connection.path
        const startPoint = path[0]
        const endPoint = path[path.length - 1]

        // Find nodes near the connection endpoints with tighter matching
        let fromNodeId: string | null = null
        let toNodeId: string | null = null
        let fromMetrics: any = null
        let toMetrics: any = null

        // Find which nodes this connection connects
        props.nodeElements.forEach((position, nodeId) => {
          const nodeCenterX = position.x + position.width / 2
          const nodeBottomY = position.y + position.height
          const nodeTopY = position.y

          // For regular nodes
          if (!nodeId.includes('split-label') && !nodeId.includes('widget')) {
            // Check connection start (usually from bottom of a node)
            const startDistX = Math.abs(startPoint.x - nodeCenterX)
            const startDistY = Math.abs(startPoint.y - nodeBottomY)

            if (startDistX < 30 && startDistY < 30) {
              fromNodeId = nodeId
              fromMetrics = nodeMetrics.value[nodeId]
            }

            // Check connection end (usually to top of a node)
            const endDistX = Math.abs(endPoint.x - nodeCenterX)
            const endDistY = Math.abs(endPoint.y - nodeTopY)

            if (endDistX < 30 && endDistY < 30) {
              toNodeId = nodeId
              toMetrics = nodeMetrics.value[nodeId]
            }
          }

          // For split labels, check if this is a split connection
          if (nodeId.includes('split-label')) {
            // Split labels connect from their center
            const splitDistX = Math.abs(startPoint.x - nodeCenterX)
            const splitDistY = Math.abs(startPoint.y - nodeCenterX)

            if (splitDistX < 50 || splitDistY < 50) {
              // This is a split connection - inherit metrics from the parent node
              fromNodeId = nodeId
              // Find the parent node metrics
              props.nodeElements.forEach((parentPos, parentId) => {
                if (
                  !parentId.includes('split-label') &&
                  !parentId.includes('widget')
                ) {
                  const parentMetrics = nodeMetrics.value[parentId]
                  if (parentMetrics && parentMetrics.visitorCount > 0) {
                    // Check if this split is below the parent
                    if (
                      Math.abs(parentPos.x + parentPos.width / 2 - position.x) <
                        100 &&
                      position.y > parentPos.y
                    ) {
                      fromMetrics = parentMetrics
                    }
                  }
                }
              })
            }
          }
        })

        // Determine path intensity based on actual traffic flow
        let pathIntensity = 0
        let shouldShowPath = false

        // Check connection type for special handling
        const isLinearSplit = connection.type === 'linear'
        const isCurvedSplit =
          connection.type &&
          (connection.type.includes('curved-start') ||
            connection.type.includes('curved-end'))
        const isSplitConnection = isLinearSplit || isCurvedSplit

        // Only show path if destination has traffic
        if (toMetrics && toMetrics.visitorCount > 0) {
          pathIntensity =
            Math.log(toMetrics.visitorCount + 1) / Math.log(maxVisitors + 1)
          shouldShowPath = true
        } else if (fromMetrics && fromMetrics.visitorCount > 0) {
          // For split connections, show heat based on source metrics
          if (isSplitConnection) {
            pathIntensity =
              Math.log(fromMetrics.visitorCount + 1) / Math.log(maxVisitors + 1)
            shouldShowPath = true

            const targetSplitEl = connection.path[1].el
            if (targetSplitEl) {
              const count =
                targetSplitEl.querySelector('.user-count-amount').innerText
              if (count == '0') {
                shouldShowPath = false
              } else {
                shouldShowPath = true
              }
            }
          } else if (!toNodeId) {
            // For paths leading to nowhere (like exit flows), don't show heat
            pathIntensity = 0
            shouldShowPath = false
          }
        }

        // Only add heat points if there's actual traffic flowing through this path
        if (shouldShowPath && pathIntensity > 0) {
          // Generate actual path points following FlowConnection.vue logic
          const pathPoints = generateFlowPath(
            startPoint,
            endPoint,
            connection.type
          )

          // Calculate desired number of heat points based on path length
          let totalPathLength = 0
          for (let i = 1; i < pathPoints.length; i++) {
            const dx = pathPoints[i].x - pathPoints[i - 1].x
            const dy = pathPoints[i].y - pathPoints[i - 1].y
            totalPathLength += Math.sqrt(dx * dx + dy * dy)
          }

          const desiredPointCount = Math.max(
            10,
            Math.floor(totalPathLength / 15)
          )

          // Interpolate points along the path segments
          for (let i = 0; i < desiredPointCount; i++) {
            const t = i / (desiredPointCount - 1)

            // Find which segment this t falls on
            const targetDistance = t * totalPathLength
            let currentDistance = 0
            let segmentIndex = 0
            let segmentT = 0

            for (let j = 1; j < pathPoints.length; j++) {
              const dx = pathPoints[j].x - pathPoints[j - 1].x
              const dy = pathPoints[j].y - pathPoints[j - 1].y
              const segmentLength = Math.sqrt(dx * dx + dy * dy)

              if (currentDistance + segmentLength >= targetDistance) {
                segmentIndex = j - 1
                segmentT = (targetDistance - currentDistance) / segmentLength
                break
              }
              currentDistance += segmentLength
            }

            // Interpolate position on the found segment
            const p1 = pathPoints[segmentIndex]
            const p2 =
              pathPoints[Math.min(segmentIndex + 1, pathPoints.length - 1)]
            const x = p1.x + (p2.x - p1.x) * segmentT
            const y = p1.y + (p2.y - p1.y) * segmentT

            // Create smooth intensity gradient along path
            const edgeFade = Math.sin(t * Math.PI) // Fade at edges
            const intensity = pathIntensity * (0.75 + 0.15 * edgeFade)
            const radius = 20 + 5 * edgeFade // Smaller radius

            points.push({
              id: `path-${index}-${i}`,
              x,
              y,
              intensity,
              radius
            })
          }
        }
      }
    })
  }

  return points
})

// Generate path points following FlowConnection.vue logic
function generateFlowPath(
  start: { x: number; y: number },
  end: { x: number; y: number },
  type?: string
): { x: number; y: number }[] {
  const points: { x: number; y: number }[] = []
  const dx = end.x - start.x
  const dy = end.y - start.y

  // Determine if the path is more horizontal or vertical
  let isHorizontal = Math.abs(dx) > Math.abs(dy)

  if (type?.includes('force') && Math.round(end.x) !== Math.round(start.x)) {
    isHorizontal = true
  }

  if (Math.abs(dx) < 1 && Math.abs(dy) < 1) {
    // Very close points - straight line
    return [start, end]
  }

  if (isHorizontal || type === 'linear') {
    if (type === 'linear') {
      // Linear connections (for splits) - step-like path
      if (start.y !== end.y) {
        const twoThirdsY = start.y + ((end.y - start.y) * 2) / 3
        const targetYForFirstSegment =
          end.y - twoThirdsY > 20 || twoThirdsY < end.y ? twoThirdsY : end.y

        // Add points for the step pattern
        points.push(start)
        points.push({ x: start.x, y: targetYForFirstSegment })
        points.push({ x: start.x, y: twoThirdsY })
        points.push({ x: end.x, y: twoThirdsY })
        points.push(end)
      } else {
        // Same height - horizontal line
        points.push(start)
        points.push({ x: start.x, y: end.y })
        points.push({ x: end.x, y: end.y })
      }
    } else {
      // Curved connections (horizontal dominant)
      const threshold = 40
      const xDelta = start.x - end.x
      const direction = xDelta > 0 ? 1 : -1

      points.push(start)
      points.push({ x: start.x, y: start.y + threshold })

      // First bezier curve points - sample the curve
      const curve1EndX = start.x + (threshold / 2) * direction * -1
      const curve1EndY = start.y + threshold + threshold / 2

      // Sample bezier curve
      for (let t = 0.1; t <= 1; t += 0.1) {
        const x = cubicBezier(t, start.x, start.x, start.x, curve1EndX)
        const y = cubicBezier(
          t,
          start.y + threshold,
          start.y + threshold,
          start.y + threshold + threshold / 2,
          curve1EndY
        )
        points.push({ x, y })
      }

      // Horizontal segment
      const newX = end.x - (threshold / 2) * direction * -1
      points.push({ x: newX, y: curve1EndY })

      // Second bezier curve points
      for (let t = 0.1; t <= 1; t += 0.1) {
        const x = cubicBezier(
          t,
          newX,
          newX,
          newX - (threshold / 2) * direction,
          newX - (threshold / 2) * direction
        )
        const y = cubicBezier(
          t,
          curve1EndY,
          curve1EndY,
          curve1EndY,
          curve1EndY + threshold / 2
        )
        points.push({ x, y })
      }

      points.push(end)
    }
  } else {
    // Vertical connections - simple straight line (as per FlowConnection)
    points.push(start)
    points.push(end)
  }

  return points
}

// Helper function for cubic bezier interpolation
function cubicBezier(
  t: number,
  p0: number,
  p1: number,
  p2: number,
  p3: number
): number {
  const t2 = t * t
  const t3 = t2 * t
  const mt = 1 - t
  const mt2 = mt * mt
  const mt3 = mt2 * mt
  return mt3 * p0 + 3 * mt2 * t * p1 + 3 * mt * t2 * p2 + t3 * p3
}
</script>

<style scoped>
.heat-map-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 5; /* Above nodes but below UI controls */
  mix-blend-mode: multiply;
  opacity: 0.9;
}

/* Subtle breathing animation for all heat points */
@keyframes heat-breathe {
  0%, 100% {
    opacity: 0.9;
  }
  50% {
    opacity: 1;
  }
}

/* Apply breathing animation to base layer */
.heat-base-layer {
  animation: heat-breathe 4s ease-in-out infinite;
}

/* The glow layer uses SVG animation, no CSS animation needed */
.heat-glow-layer {
  mix-blend-mode: overlay;
}

/* Add slight scale animation to individual points for depth */
.heat-point {
  transform-origin: center;
  transition: transform 0.3s ease;
}

.heat-point:hover {
  transform: scale(1.1);
}
</style>